import type { AttributeValue, EntityLevel, FieldDescriptor, FieldValueType, LayerModel, OperationalAttributeField, OriginalAttributes } from '../core/types';
import { geodesicArea, NICARAGUAN_MANZANA_M2 } from './measurement';

export interface EffectiveAttributeCatalog {
  readonly fields: readonly FieldDescriptor[];
  readonly attributesBySuerteId: ReadonlyMap<string, OriginalAttributes>;
}

const displayValue = (value: AttributeValue | undefined): string => value === null || value === undefined || value === '' ? 'Sin valor' : String(value);
const enc = (value: string): string => encodeURIComponent(value);
const sourceDescriptorCache = new WeakMap<LayerModel, readonly FieldDescriptor[]>();
const logicalGeometryAreaCache = new WeakMap<LayerModel, Map<string, number>>();
export const sourceFieldId = (layerId: string, name: string): string => `source:${enc(layerId)}:${enc(name)}`;
export const normalizedFieldName = (name: string): string => name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '').toLocaleLowerCase();
const MASTER_FIELD_LABELS: Readonly<Record<string, string>> = {
  nombre: 'Nombre', codsuerte: 'Código de Suerte', suerte: 'Suerte', tablon: 'Tablón', areaha: 'Área_Ha original', areamz: 'Área_Mz original',
  finca: 'Finca', codfinca: 'Código de finca', renov: 'Renovación', renovacion: 'Renovación', variedad: 'Variedad', trie: 'Tipo de riego', tiporiego: 'Tipo de riego', tipoderiego: 'Tipo de riego',
  edad: 'Edad', tsue: 'Textura', textura: 'Textura', tch: 'TCH', tchact: 'TCH', fsiembra: 'Fecha de siembra', fechadesiembra: 'Fecha de siembra',
  fcosecha: 'Fecha de cosecha', fechadecosecha: 'Fecha de cosecha', zon: 'Zona', zona: 'Zona', nc: 'NC', coordx: 'Coordenada X', coordy: 'Coordenada Y',
};
const HIDDEN_SOURCE_FIELDS = new Set(['description', 'descripcion', 'fid', 'id', 'objectid', 'ogcfid']);

const PHYSICAL_MEASURE_FIELDS = new Set(['areaha', 'areamz', 'area', 'shapearea', 'shapeleng', 'shapelength', 'perimetro', 'perimeter', 'hectareas', 'manzanas']);
/** Per-fragment physical measures (e.g. Area_Ha): never an attribute of a Finca/Suerte/Tablón as a whole. */
export const isPhysicalMeasureField = (name: string): boolean => PHYSICAL_MEASURE_FIELDS.has(normalizedFieldName(name));
export const masterSourceFieldLabel = (name: string): string => MASTER_FIELD_LABELS[normalizedFieldName(name)] ?? name.replaceAll('_', ' ');
export const isUserVisibleSourceField = (name: string): boolean => {
  const trimmed = name.trim();
  const lower = trimmed.toLocaleLowerCase();
  if (!trimmed || trimmed.startsWith('_') || lower.startsWith('system:') || lower.startsWith('source:')) return false;
  return !HIDDEN_SOURCE_FIELDS.has(normalizedFieldName(trimmed));
};
export const SYSTEM_AREA_FIELD_IDS = {
  suerteHa: 'system:derived:suerte:area-ha',
  suerteMz: 'system:derived:suerte:area-mz',
  tablonHa: 'system:derived:tablon:area-ha',
  tablonMz: 'system:derived:tablon:area-mz',
  fincaHa: 'system:derived:finca:area-active-ha',
  fincaDeclaredHa: 'system:derived:finca:area-declared-ha',
  suerteDeclaredHa: 'system:derived:suerte:area-declared-ha',
  tablonDeclaredHa: 'system:derived:tablon:area-declared-ha',
  fragmentHa: 'system:derived:fragment:area-active-ha',
} as const;

export const SYSTEM_LOGICAL_FIELD_IDS = {
  fincaName: 'system:logical:finca:name',
  fincaCode: 'system:logical:finca:code',
  suerteLabel: 'system:logical:suerte:label',
  suerteCode: 'system:logical:suerte:code',
  tablonCode: 'system:logical:tablon:code',
  fragmentId: 'system:logical:fragment:id',
} as const;

const LOGICAL_FIELDS: readonly FieldDescriptor[] = [
  { id: SYSTEM_LOGICAL_FIELD_IDS.fincaName, name: 'Nombre de finca', source: 'system', type: 'string', level: 'finca', sourceName: 'GeoStudio' },
  { id: SYSTEM_LOGICAL_FIELD_IDS.fincaCode, name: 'Código de finca', source: 'system', type: 'string', level: 'finca', sourceName: 'GeoStudio' },
  { id: SYSTEM_LOGICAL_FIELD_IDS.suerteLabel, name: 'Suerte', source: 'system', type: 'string', level: 'suerte', sourceName: 'GeoStudio' },
  { id: SYSTEM_LOGICAL_FIELD_IDS.suerteCode, name: 'Código de Suerte', source: 'system', type: 'string', level: 'suerte', sourceName: 'GeoStudio' },
  { id: SYSTEM_LOGICAL_FIELD_IDS.tablonCode, name: 'Tablón', source: 'system', type: 'string', level: 'tablon', sourceName: 'GeoStudio' },
  { id: SYSTEM_LOGICAL_FIELD_IDS.fragmentId, name: 'ID de fragmento', source: 'system', type: 'string', level: 'fragment', sourceName: 'GeoStudio' },
];

const SYSTEM_FIELDS: readonly FieldDescriptor[] = [
  { id: SYSTEM_AREA_FIELD_IDS.suerteHa, name: 'Área geométrica activa de suerte (ha)', source: 'system', type: 'number', level: 'suerte', sourceName: 'GeoStudio' },
  { id: SYSTEM_AREA_FIELD_IDS.suerteMz, name: 'Área geométrica activa de suerte (mz)', source: 'system', type: 'number', level: 'suerte', sourceName: 'GeoStudio' },
  { id: SYSTEM_AREA_FIELD_IDS.tablonHa, name: 'Área geométrica activa de tablón (ha)', source: 'system', type: 'number', level: 'tablon', sourceName: 'GeoStudio' },
  { id: SYSTEM_AREA_FIELD_IDS.tablonMz, name: 'Área geométrica activa de tablón (mz)', source: 'system', type: 'number', level: 'tablon', sourceName: 'GeoStudio' },
  { id: SYSTEM_AREA_FIELD_IDS.fincaHa, name: 'Área geométrica activa (ha)', source: 'system', type: 'number', level: 'finca', sourceName: 'GeoStudio' },
  { id: SYSTEM_AREA_FIELD_IDS.fincaDeclaredHa, name: 'Área declarada/original (ha)', source: 'system', type: 'number', level: 'finca', sourceName: 'GeoStudio' },
  { id: SYSTEM_AREA_FIELD_IDS.suerteDeclaredHa, name: 'Área declarada/original (ha)', source: 'system', type: 'number', level: 'suerte', sourceName: 'GeoStudio' },
  { id: SYSTEM_AREA_FIELD_IDS.tablonDeclaredHa, name: 'Área declarada/original (ha)', source: 'system', type: 'number', level: 'tablon', sourceName: 'GeoStudio' },
  { id: SYSTEM_AREA_FIELD_IDS.fragmentHa, name: 'Área geométrica activa (ha)', source: 'system', type: 'number', level: 'fragment', sourceName: 'GeoStudio' },
];

export const logicalFieldDescriptors = (): readonly FieldDescriptor[] => LOGICAL_FIELDS;
export const systemFieldDescriptors = (): readonly FieldDescriptor[] => [...LOGICAL_FIELDS, ...SYSTEM_FIELDS];
export const fieldDisplayName = (field: FieldDescriptor): string => `${field.name} · ${field.source === 'system' ? 'sistema' : field.source === 'source' ? 'fuente' : field.source === 'join' ? 'Excel' : 'proyecto'}`;

export function logicalAreaAttributes(model: LayerModel, ids: { fincaId?: string; suerteId?: string; tablonId?: string; fragmentId?: string }): OriginalAttributes {
  const finca = ids.fincaId ? model.fincas.get(ids.fincaId) : undefined;
  const suerte = ids.suerteId ? model.suertes.get(ids.suerteId) : undefined;
  const tablon = ids.tablonId ? model.tablones.get(ids.tablonId) : undefined;
  const fragment = ids.fragmentId ? model.fragmentsById.get(ids.fragmentId) : undefined;
  const values: Record<string, AttributeValue> = {};
  const geometryAreaHa = (key: string, fragmentIds: readonly string[]): number => {
    let cache = logicalGeometryAreaCache.get(model);
    if (!cache) { cache = new Map(); logicalGeometryAreaCache.set(model, cache); }
    const cached = cache.get(key); if (cached !== undefined) return cached;
    const area = fragmentIds.reduce((sum, fragmentId) => {
    const geometry = model.fragmentsById.get(fragmentId)?.feature.getGeometry();
    return sum + (geometry ? geodesicArea(geometry) / 10_000 : 0);
    }, 0);
    cache.set(key, area); return area;
  };
  if (finca) {
    values[SYSTEM_AREA_FIELD_IDS.fincaHa] = geometryAreaHa(finca.id, finca.fragmentIds);
    values[SYSTEM_AREA_FIELD_IDS.fincaDeclaredHa] = finca.declaredAreaHa;
  }
  if (suerte) {
    const activeAreaHa = geometryAreaHa(suerte.id, suerte.fragmentIds);
    values[SYSTEM_AREA_FIELD_IDS.suerteHa] = activeAreaHa;
    values[SYSTEM_AREA_FIELD_IDS.suerteMz] = activeAreaHa * 10_000 / NICARAGUAN_MANZANA_M2;
    values[SYSTEM_AREA_FIELD_IDS.suerteDeclaredHa] = suerte.declaredAreaHa;
    values['Área de suerte (ha)'] = activeAreaHa;
    values['Área de suerte (mz)'] = activeAreaHa * 10_000 / NICARAGUAN_MANZANA_M2;
  }
  if (tablon) {
    const activeAreaHa = geometryAreaHa(tablon.id, tablon.fragmentIds);
    values[SYSTEM_AREA_FIELD_IDS.tablonHa] = activeAreaHa;
    values[SYSTEM_AREA_FIELD_IDS.tablonMz] = activeAreaHa * 10_000 / NICARAGUAN_MANZANA_M2;
    values[SYSTEM_AREA_FIELD_IDS.tablonDeclaredHa] = tablon.declaredAreaHa;
    values['Área de tablón (ha)'] = activeAreaHa;
    values['Área de tablón (mz)'] = activeAreaHa * 10_000 / NICARAGUAN_MANZANA_M2;
  }
  if (fragment) values[SYSTEM_AREA_FIELD_IDS.fragmentHa] = geometryAreaHa(fragment.id, [fragment.id]);
  return values;
}

export function logicalIdentityAttributes(model: LayerModel, ids: { fincaId?: string; suerteId?: string; tablonId?: string; fragmentId?: string }): OriginalAttributes {
  const finca = ids.fincaId ? model.fincas.get(ids.fincaId) : undefined;
  const suerte = ids.suerteId ? model.suertes.get(ids.suerteId) : undefined;
  const tablon = ids.tablonId ? model.tablones.get(ids.tablonId) : undefined;
  return {
    ...(finca ? {
      [SYSTEM_LOGICAL_FIELD_IDS.fincaName]: finca.name,
      [SYSTEM_LOGICAL_FIELD_IDS.fincaCode]: finca.code,
      Finca: finca.name,
      Nombre: finca.name,
      Codfinca: finca.code,
    } : {}),
    ...(suerte ? {
      [SYSTEM_LOGICAL_FIELD_IDS.suerteLabel]: suerte.label,
      [SYSTEM_LOGICAL_FIELD_IDS.suerteCode]: suerte.code,
      Suerte: suerte.label,
      CodSuerte: suerte.code,
    } : {}),
    ...(tablon ? { [SYSTEM_LOGICAL_FIELD_IDS.tablonCode]: tablon.code, Tablon: tablon.code } : {}),
    ...(ids.fragmentId ? { [SYSTEM_LOGICAL_FIELD_IDS.fragmentId]: ids.fragmentId } : {}),
  };
}

export function inferFieldType(values: readonly AttributeValue[]): FieldValueType {
  const populated = values.filter((value) => value !== null && value !== '');
  if (populated.length && populated.every((value) => typeof value === 'number' && Number.isFinite(value))) return 'number';
  if (populated.length && populated.every((value) => typeof value === 'boolean')) return 'boolean';
  if (populated.length && populated.every((value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value))) return 'date';
  return 'string';
}

function inferSourceFieldType(name: string, values: readonly AttributeValue[]): FieldValueType {
  const inferred = inferFieldType(values);
  if (inferred !== 'string') return inferred;
  const normalized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '').toLocaleLowerCase();
  const identifier = normalized === 'id' || normalized === 'fid' || normalized === 'finca' || normalized === 'suerte' || normalized === 'tablon' || normalized.startsWith('cod') || normalized.endsWith('id');
  const populated = values.filter((value) => value !== null && value !== '');
  return !identifier && populated.length > 0 && populated.every((value) => typeof value === 'string' && Number.isFinite(Number(value.trim()))) ? 'number' : 'string';
}

export function sourceFieldDescriptors(model: LayerModel): readonly FieldDescriptor[] {
  const cached = sourceDescriptorCache.get(model);
  if (cached) return cached;
  const descriptors: readonly FieldDescriptor[] = model.attributeFields.map((name) => ({
    id: sourceFieldId(model.id, name), name, source: 'source', level: 'fragment', sourceName: model.name,
    type: inferSourceFieldType(name, model.fragments.map(({ attributes }) => attributes[name] ?? null)),
  }));
  sourceDescriptorCache.set(model, descriptors);
  return descriptors;
}

const fieldCatalogCache = new WeakMap<LayerModel, { readonly fields: readonly OperationalAttributeField[]; readonly catalog: readonly FieldDescriptor[] }>();
/** Memoized by (model, operational-field list) references: both are immutable and replaced on change. */
export function effectiveFieldCatalog(model: LayerModel, fields: readonly OperationalAttributeField[] = []): readonly FieldDescriptor[] {
  const cached = fieldCatalogCache.get(model);
  if (cached && (cached.fields === fields || (!cached.fields.length && !fields.length))) return cached.catalog;
  const catalog = [...systemFieldDescriptors(), ...sourceFieldDescriptors(model), ...fields.map(({ descriptor }) => descriptor)];
  fieldCatalogCache.set(model, { fields, catalog });
  return catalog;
}

export function representativeAttributes(model: LayerModel, fragmentIds: readonly string[], overrides: OriginalAttributes = {}): OriginalAttributes {
  const result: Record<string, AttributeValue> = {};
  model.attributeFields.forEach((field) => {
    const counts = new Map<string, { value: AttributeValue; count: number }>();
    fragmentIds.forEach((id) => {
      const value = model.fragmentsById.get(id)?.attributes[field];
      if (value === null || value === undefined || value === '') return;
      const key = String(value); const current = counts.get(key);
      counts.set(key, { value, count: (current?.count ?? 0) + 1 });
    });
    const value = [...counts.values()].sort((a, b) => b.count - a.count || String(a.value).localeCompare(String(b.value), 'es', { numeric: true }))[0]?.value;
    if (value !== undefined) result[field] = value;
  });
  return { ...result, ...overrides };
}

export const entityKey = (level: EntityLevel, ids: { fincaId?: string; suerteId?: string; tablonId?: string; fragmentId?: string; featureId?: string; subareaId?: string }): string | null =>
  level === 'finca' ? ids.fincaId ?? null : level === 'suerte' ? ids.suerteId ?? null : level === 'tablon' ? ids.tablonId ?? null : level === 'fragment' ? ids.fragmentId ?? null : level === 'subarea' ? ids.subareaId ?? null : ids.featureId ?? null;

export function operationalValue(field: OperationalAttributeField, ids: Parameters<typeof entityKey>[1]): AttributeValue | undefined {
  const key = entityKey(field.descriptor.level, ids);
  return key ? field.valuesByEntityId[key] : undefined;
}

/** Most frequent non-empty value of one source field among fragments (same rule as representativeAttributes). */
function representativeValue(model: LayerModel, fragmentIds: readonly string[], field: string): AttributeValue | undefined {
  const counts = new Map<string, { value: AttributeValue; count: number }>();
  fragmentIds.forEach((id) => {
    const value = model.fragmentsById.get(id)?.attributes[field];
    if (value === null || value === undefined || value === '') return;
    const key = String(value); const current = counts.get(key);
    counts.set(key, { value, count: (current?.count ?? 0) + 1 });
  });
  if (counts.size <= 1) return counts.values().next().value?.value;
  return [...counts.values()].sort((a, b) => b.count - a.count || String(a.value).localeCompare(String(b.value), 'es', { numeric: true }))[0]?.value;
}

const sourceDescriptorIndex = new WeakMap<LayerModel, ReadonlyMap<string, FieldDescriptor>>();
const sourceDescriptorFor = (model: LayerModel, fieldId: string): FieldDescriptor | undefined => {
  let index = sourceDescriptorIndex.get(model);
  if (!index) { const map = new Map<string, FieldDescriptor>(); sourceFieldDescriptors(model).forEach((descriptor) => { map.set(descriptor.id, descriptor); if (!map.has(descriptor.name)) map.set(descriptor.name, descriptor); }); index = map; sourceDescriptorIndex.set(model, index); }
  return index.get(fieldId);
};

export function fieldValueForMasterEntity(model: LayerModel, fieldId: string, fragmentIds: readonly string[], ids: Parameters<typeof entityKey>[1], fields: readonly OperationalAttributeField[] = []): AttributeValue | undefined {
  // Prefixed ids can never be logical identity/area keys: skip those (geodesic) computations entirely.
  const prefixed = fieldId.startsWith('source:') || fieldId.startsWith('join:') || fieldId.startsWith('project:');
  if (!prefixed) {
    const logical = logicalIdentityAttributes(model, ids)[fieldId];
    if (logical !== undefined) return logical;
    const system = logicalAreaAttributes(model, ids)[fieldId];
    if (system !== undefined) return system;
  }
  const operational = fields.find(({ descriptor }) => descriptor.id === fieldId);
  if (operational) return operationalValue(operational, ids);
  const descriptor = sourceDescriptorFor(model, fieldId);
  return descriptor ? representativeValue(model, fragmentIds, descriptor.name) : undefined;
}

export const attributeCategory = (attributes: OriginalAttributes, field: string): string => displayValue(attributes[field]);

export function resolveEffectiveAttributes(model: LayerModel, fields: readonly OperationalAttributeField[] = []): EffectiveAttributeCatalog {
  const descriptors = effectiveFieldCatalog(model, fields);
  const attributesBySuerteId = new Map<string, OriginalAttributes>();
  model.suertes.forEach((suerte) => attributesBySuerteId.set(suerte.id, effectiveAttributesForEntity(model, suerte.fragmentIds, { fincaId: suerte.fincaId, suerteId: suerte.id }, fields)));
  return { fields: descriptors, attributesBySuerteId };
}

export function effectiveAttributeFields(model: LayerModel, fields: readonly OperationalAttributeField[] = []): readonly string[] {
  return effectiveFieldCatalog(model, fields).map(({ id }) => id);
}

export function effectiveAttributesForEntity(model: LayerModel, fragmentIds: readonly string[], ids: Parameters<typeof entityKey>[1], fields: readonly OperationalAttributeField[] = [], overrides: OriginalAttributes = {}): OriginalAttributes {
  const result: Record<string, AttributeValue> = { ...representativeAttributes(model, fragmentIds), ...logicalIdentityAttributes(model, ids), ...logicalAreaAttributes(model, ids) };
  fields.forEach((field) => {
    const value = operationalValue(field, ids);
    if (value !== undefined) { result[field.descriptor.id] = value; if (!(field.descriptor.name in result)) result[field.descriptor.name] = value; }
  });
  sourceFieldDescriptors(model).forEach((field) => { if (field.name in result) result[field.id] = result[field.name]!; });
  return { ...result, ...overrides };
}

interface NumericTemplateFormat { readonly decimals: 'auto' | 0 | 1 | 2 | 3 | 4; readonly prefix: string; readonly suffix: string; readonly unit: string }

export function formatNumericAttribute(value: number, format: NumericTemplateFormat): string {
  const numeric = format.decimals === 'auto'
    ? String(value)
    : value.toLocaleString('es-NI', { minimumFractionDigits: format.decimals, maximumFractionDigits: format.decimals });
  return `${format.prefix}${numeric}${format.suffix}${format.unit ? ` ${format.unit}` : ''}`;
}

export function formatAttributeTemplate(template: string, attributes: OriginalAttributes, numericFormats: Readonly<Record<string, NumericTemplateFormat>> = {}): string {
  return template.split(/\r?\n/).slice(0, 3).map((line) => line.replace(/\{([^{}]+)\}/g, (_, field: string) => {
    const key = field.trim(); const value = attributes[key];
    if (value === null || value === undefined) return '';
    const format = numericFormats[key]; const numeric = typeof value === 'number' ? value : Number(value);
    return format && Number.isFinite(numeric) ? formatNumericAttribute(numeric, format) : String(value);
  }).trim()).filter(Boolean).join('\n');
}
